import {hasOwn} from '@pexip/utils';

import type {SSOdMessage, MediaPriority, RoomID, ParticipantID} from './types';
import {GUID_MIN_LENGTH} from './constants';

export const isUndefined = <T>(t: T | undefined): t is undefined =>
    typeof t === 'undefined';

export const isNull = <T>(t: T | undefined | null): t is null =>
    typeof t === 'object' && !t;

export const isNotUndefined = <T>(t: T | undefined | null): t is T =>
    !isUndefined(t) && !isNull(t);

export const isSSOdMessage = (e: unknown): e is SSOdMessage => {
    if (typeof e !== 'object') {
        return false;
    }
    if (e === null) {
        return false;
    }
    if ('result' in e && ('token' in e || 'code' in e)) {
        return true;
    }
    return false;
};

const RTC_PRIORITY = ['very-low', 'low', 'medium', 'high'] as const;

export const isMediaPriority = (e: unknown): e is MediaPriority => {
    if (!e || typeof e !== 'object') {
        return false;
    }
    if (hasOwn(e, 'video') && hasOwn(e, 'audio') && hasOwn(e, 'presentation')) {
        return !Object.values(e).some(
            e => !RTC_PRIORITY.includes(e as RTCPriorityType),
        );
    }
    return false;
};

export function isRoomID(id: string | undefined): id is RoomID {
    if (id && (id === 'main' || id.trim().length >= GUID_MIN_LENGTH)) {
        return true;
    }
    return false;
}

// Assert cannot be arrow function: https://github.com/microsoft/TypeScript/issues/34523
export function assertRoomID(id: string): asserts id is RoomID {
    if (!isRoomID(id)) {
        throw new Error('Invalid RoomID');
    }
}

export function isParticipantID(id: string | undefined): id is ParticipantID {
    if (id && id.trim().length >= GUID_MIN_LENGTH) {
        return true;
    }
    return false;
}

export function assertParticipantID(id: string): asserts id is ParticipantID {
    if (!isParticipantID(id)) {
        throw new Error('Invalid ParticipantID');
    }
}
